import { useEffect, useState } from 'react';
import type { Calendar } from '../../shared/contracts';
import type {
  ParticipationForm,
  ParticipationFormQuestion,
  ParticipationFormResults,
  ParticipationFormResponse,
  FormAnswers,
} from '../../shared/forms';
import type { ClientSession } from '../api';
import { participationRequest } from '../participation-api';
import { QuestionFields } from './ParticipationPage';
import '../components/participation.css';
type QuestionType = ParticipationFormQuestion['type'];
const newQuestion = (type: QuestionType): ParticipationFormQuestion => ({
  id: crypto.randomUUID(),
  label: '',
  description: '',
  required: true,
  ...(type === 'rating'
    ? { type, min: 1, max: 5 }
    : type === 'single' || type === 'multiple' || type === 'availability'
      ? {
          type,
          options: [
            { id: crypto.randomUUID(), label: '' },
            { id: crypto.randomUUID(), label: '' },
          ],
        }
      : { type }),
});
export function ParticipationManagePage({
  session,
  calendars,
  formId,
}: {
  session: ClientSession | null;
  calendars: Calendar[];
  formId?: string;
}) {
  const context = new URLSearchParams(location.search);
  const eventContext = context.get('event');
  const editable = calendars.filter((calendar) => calendar.canEdit);
  const [form, setForm] = useState<ParticipationForm>();
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [calendarId, setCalendarId] = useState(
    context.get('calendar') && editable.some((calendar) => calendar.id === context.get('calendar'))
      ? context.get('calendar')!
      : editable[0]?.id || '',
  );
  const [kind, setKind] = useState<'poll' | 'feedback'>('poll');
  const [identityMode, setIdentityMode] = useState<'identified' | 'anonymous'>('identified');
  const [pilot, setPilot] = useState(false);
  const audienceCalendar = calendars.find((calendar) => calendar.id === calendarId);
  const [questions, setQuestions] = useState<ParticipationFormQuestion[]>([newQuestion('single')]);
  const [closesAt, setClosesAt] = useState('');
  const [opensAt, setOpensAt] = useState('');
  const [questionType, setQuestionType] = useState<QuestionType>('short-text');
  const [preview, setPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState<FormAnswers>({});
  const [results, setResults] = useState<ParticipationFormResults>();
  const [readers, setReaders] =
    useState<{ id: string; name: string; email: string; selected: boolean }[]>();
  const [readerVersion, setReaderVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const adopt = (value: ParticipationForm) => {
    setForm(value);
    setTitle(value.title);
    setIntro(value.intro);
    setCalendarId(value.calendarId);
    setKind(value.kind);
    setIdentityMode(value.identityMode);
    setPilot(value.pilot);
    setOpensAt(
      value.opensAt
        ? new Date(
            new Date(value.opensAt).getTime() - new Date(value.opensAt).getTimezoneOffset() * 60000,
          )
            .toISOString()
            .slice(0, 16)
        : '',
    );
    setQuestions(value.questions);
    setClosesAt(
      value.closesAt
        ? new Date(
            new Date(value.closesAt).getTime() -
              new Date(value.closesAt).getTimezoneOffset() * 60000,
          )
            .toISOString()
            .slice(0, 16)
        : '',
    );
  };
  useEffect(() => {
    if (formId)
      void participationRequest<{ form: ParticipationForm }>(
        `/api/participation/forms/${encodeURIComponent(formId)}`,
        session,
      )
        .then(({ form: value }) => adopt(value))
        .catch((cause) => setError(cause.message));
  }, [formId]);
  const updateQuestion = (index: number, question: ParticipationFormQuestion) =>
    setQuestions(questions.map((item, i) => (i === index ? question : item)));
  const save = async (status = 'draft', clone = false) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const body = {
        ...(!form ? { calendarId, kind, eventId: eventContext || null } : {}),
        title,
        intro,
        identityMode,
        pilot,
        questions,
        closesAt: closesAt ? new Date(closesAt).toISOString() : null,
        opensAt: opensAt ? new Date(opensAt).toISOString() : null,
        status,
        ...(form ? { version: form.version } : {}),
        ...(clone
          ? { action: 'clone-version' }
          : form?.status === 'closed' && status === 'open'
            ? { action: 'reopen' }
            : {}),
      };
      const result = await participationRequest<{ form: ParticipationForm }>(
        form ? `/api/participation/forms/${form.id}` : '/api/participation/forms',
        session,
        form ? 'PATCH' : 'POST',
        body,
      );
      adopt(result.form);
      setNotice(
        status === 'scheduled'
          ? 'Scheduled. Responses will open at the selected time.'
          : status === 'open'
            ? 'Published. Eligible participants can now respond.'
            : status === 'closed'
              ? 'Closed. Existing responses are kept.'
              : 'Draft saved.',
      );
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const readResults = async () => {
    if (!form) return;
    try {
      setResults(
        await participationRequest(`/api/participation/forms/${form.id}/results`, session),
      );
      setError('');
    } catch (cause) {
      setError((cause as Error).message);
    }
  };
  if (!session?.user)
    return (
      <main id="main-content" className="participation-page">
        <h1>Sign in to create polls and forms</h1>
        <a href="/login">Sign in</a>
      </main>
    );
  if (form && !form.canManage)
    return (
      <main id="main-content" className="participation-page">
        <h1>Organizer access required</h1>
        <a href={form.path}>Back to the form</a>
      </main>
    );
  return (
    <main id="main-content" className="participation-page">
      <a href="/participation">← Polls and feedback</a>
      <h1>{form ? 'Manage poll or form' : 'Create a poll or form'}</h1>
      {eventContext && <p>This poll or form will be linked to the selected event.</p>}
      <form
        className="participation-card"
        onSubmit={(event) => {
          event.preventDefault();
          void save(form?.status || 'draft');
        }}
      >
        <label className="field">
          Calendar
          <select
            required
            disabled={Boolean(form)}
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
          >
            {editable.map((calendar) => (
              <option key={calendar.id} value={calendar.id}>
                {calendar.name}
              </option>
            ))}
          </select>
        </label>
        {audienceCalendar && (
          <div className="participation-notice" aria-label="Form audience">
            <strong>Who can open and respond</strong>
            <p>
              {pilot
                ? `Authorized staff in the ${audienceCalendar.name} pilot.`
                : audienceCalendar.staffOnly
                  ? `Staff with access to ${audienceCalendar.name}. Sign-in is required.`
                  : audienceCalendar.visibility === 'private'
                    ? `People with access to ${audienceCalendar.name}. Sign-in is required.`
                    : 'This is a public form. Anyone can open it.'}
            </p>
            <p>
              Choose a private group calendar, such as Board or First Grade, to limit the audience
              to that group. Sharing the form link does not grant membership.
              {identityMode === 'identified' && ' Responding requires a verified account.'}
            </p>
          </div>
        )}
        <label className="field">
          Type
          <select
            disabled={Boolean(form)}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'poll' | 'feedback')}
          >
            <option value="poll">Poll</option>
            <option value="feedback">Feedback form</option>
          </select>
        </label>
        <label className="field">
          Title
          <input
            required
            maxLength={160}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="field">
          Introduction
          <textarea maxLength={4000} value={intro} onChange={(e) => setIntro(e.target.value)} />
        </label>
        <label className="field">
          Response privacy
          <select
            value={identityMode}
            onChange={(e) => setIdentityMode(e.target.value as 'identified' | 'anonymous')}
          >
            <option value="identified">Named responses, private to result readers</option>
            <option value="anonymous">Anonymous answers with a private receipt</option>
          </select>
        </label>
        {identityMode === 'anonymous' && (
          <p className="participation-private-note">
            Do not ask for names, contact details or other identifying information. Anonymous
            collection must also be enabled for this environment. Small groups and free text can
            reveal identity.
          </p>
        )}
        <label className="field">
          Opening date (optional)
          <input
            type="datetime-local"
            value={opensAt}
            onChange={(e) => setOpensAt(e.target.value)}
          />
        </label>
        <label className="field">
          Closing date (optional)
          <input
            type="datetime-local"
            value={closesAt}
            onChange={(e) => setClosesAt(e.target.value)}
          />
        </label>
        <label>
          <input type="checkbox" checked={pilot} onChange={(e) => setPilot(e.target.checked)} />{' '}
          Staff pilot
        </label>
        <h2>Questions</h2>
        {questions.map((question, index) => (
          <fieldset className="participation-question" key={question.id}>
            <legend>Question {index + 1}</legend>
            <label className="field">
              Question text
              <input
                required
                maxLength={500}
                value={question.label}
                onChange={(e) => updateQuestion(index, { ...question, label: e.target.value })}
              />
            </label>
            <label className="field">
              Help text
              <textarea
                maxLength={2000}
                value={question.description}
                onChange={(e) =>
                  updateQuestion(index, { ...question, description: e.target.value })
                }
              />
            </label>
            <label>
              <input
                type="checkbox"
                checked={question.required}
                onChange={(e) => updateQuestion(index, { ...question, required: e.target.checked })}
              />{' '}
              Required answer
            </label>
            {'options' in question && (
              <>
                {question.options.map((option, optionIndex) => (
                  <div className="participation-inline" key={option.id}>
                    <label className="field">
                      {question.type === 'availability' ? 'Date and time option' : 'Choice'}{' '}
                      {optionIndex + 1}
                      <input
                        required
                        maxLength={200}
                        value={option.label}
                        onChange={(e) =>
                          updateQuestion(index, {
                            ...question,
                            options: question.options.map((item, i) =>
                              i === optionIndex ? { ...item, label: e.target.value } : item,
                            ),
                          })
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="button button-quiet"
                      aria-label={`Remove choice ${optionIndex + 1} from question ${index + 1}`}
                      disabled={question.options.length <= 2}
                      onClick={() =>
                        updateQuestion(index, {
                          ...question,
                          options: question.options.filter((_, i) => i !== optionIndex),
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="button button-quiet"
                  onClick={() =>
                    updateQuestion(index, {
                      ...question,
                      options: [...question.options, { id: crypto.randomUUID(), label: '' }],
                    })
                  }
                >
                  Add choice
                </button>
              </>
            )}
            {question.type === 'rating' && (
              <div className="participation-inline">
                <label className="field">
                  Lowest rating
                  <input
                    type="number"
                    min={0}
                    max={9}
                    value={question.min}
                    onChange={(e) =>
                      updateQuestion(index, { ...question, min: Number(e.target.value) })
                    }
                  />
                </label>
                <label className="field">
                  Highest rating
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={question.max}
                    onChange={(e) =>
                      updateQuestion(index, { ...question, max: Number(e.target.value) })
                    }
                  />
                </label>
              </div>
            )}
            <button
              type="button"
              className="button button-quiet"
              disabled={questions.length <= 1}
              onClick={() => setQuestions(questions.filter((_, i) => i !== index))}
            >
              Remove question
            </button>
          </fieldset>
        ))}
        <div className="participation-inline">
          <label className="field">
            New question type
            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value as QuestionType)}
            >
              <option value="short-text">Short answer</option>
              <option value="long-text">Long answer</option>
              <option value="single">Choose one</option>
              <option value="multiple">Choose several</option>
              <option value="availability">Date and time availability</option>
              <option value="rating">Rating</option>
              <option value="date">Date</option>
            </select>
          </label>
          <button
            type="button"
            className="button button-quiet"
            onClick={() => setQuestions([...questions, newQuestion(questionType)])}
          >
            Add question
          </button>
        </div>
        <div className="participation-actions">
          <button className="button" disabled={busy}>
            {busy ? 'Saving…' : form?.status === 'open' ? 'Save changes' : 'Save draft'}
          </button>
          <button
            type="button"
            className="button button-quiet"
            onClick={() => setPreview(!preview)}
          >
            {preview ? 'Hide preview' : 'Preview response form'}
          </button>
        </div>
      </form>
      {preview && (
        <section className="participation-card" aria-label="Respondent preview">
          <h2>Respondent preview</h2>
          <p>This preview does not submit a response.</p>
          <h3>{title || 'Untitled form'}</h3>
          <p>{intro}</p>
          <QuestionFields
            questions={questions}
            answers={previewAnswers}
            change={(id, answer) => setPreviewAnswers({ ...previewAnswers, [id]: answer })}
          />
        </section>
      )}
      {form && (
        <section className="participation-card">
          <h2>Publish and manage</h2>
          <p>Current status: {form.effectiveStatus || form.status}</p>
          <p>
            <a href={form.path}>Open respondent page</a>
          </p>
          <div className="participation-actions">
            <button
              className="button"
              disabled={busy || !preview}
              onClick={() =>
                void save(opensAt && Date.parse(opensAt) > Date.now() ? 'scheduled' : 'open')
              }
            >
              {opensAt && Date.parse(opensAt) > Date.now()
                ? 'Schedule opening'
                : form.status === 'closed'
                  ? 'Reopen for responses'
                  : 'Publish for responses'}
            </button>
            <button
              className="button button-quiet"
              disabled={busy}
              onClick={() => void save('closed')}
            >
              Close responses
            </button>
            <button
              className="button button-quiet"
              disabled={busy}
              onClick={() => void save('draft', true)}
            >
              Create a new version
            </button>
          </div>
          <p>
            Preview before publishing. Meaning-changing edits after responses require a new version.
          </p>
        </section>
      )}
      {form?.canReadResults && (
        <section className="participation-card">
          <h2>Private results</h2>
          <button className="button" onClick={() => void readResults()}>
            View results
          </button>{' '}
          <a href={`/api/participation/forms/${form.id}/export`}>Export responses</a>
          {results && (
            <>
              {!results.suppressed && <p>Responses in this version: {results.responseCount}</p>}
              {results.suppressed ? (
                <p>Results are withheld to protect a small group.</p>
              ) : (
                <>
                  <PollTotals
                    questions={questions}
                    responses={results.responses.filter(
                      (response) =>
                        response.formVersion === form.formVersion && !response.withdrawn,
                    )}
                  />
                  <h3>Individual responses</h3>
                  <ul>
                    {results.responses
                      ?.filter(
                        (response) =>
                          response.formVersion === form.formVersion && !response.withdrawn,
                      )
                      .map((response) => (
                        <li key={response.id}>
                          <strong>{response.participant?.name || 'Anonymous response'}</strong>
                          <dl>
                            {questions.map((question) => (
                              <div key={question.id}>
                                <dt>{question.label}</dt>
                                <dd>{displayAnswer(question, response.answers[question.id])}</dd>
                              </div>
                            ))}
                          </dl>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </>
          )}
        </section>
      )}
      {form && (
        <details
          className="participation-card"
          onToggle={(event) => {
            if (event.currentTarget.open && !readers)
              void participationRequest<{
                readers: { id: string; name: string; email: string; selected: boolean }[];
                version: number;
              }>(`/api/participation/forms/${form.id}/readers`, session)
                .then((result) => {
                  setReaders(result.readers);
                  setReaderVersion(result.version);
                })
                .catch((cause) => setError(cause.message));
          }}
        >
          <summary>Manage result readers</summary>
          <p>
            Only selected people can read and export responses. The creator retains access.
            Administrators and calendar managers can change this list.
          </p>
          {readers?.map((reader) => (
            <label key={reader.id}>
              <input
                type="checkbox"
                checked={reader.selected}
                onChange={(event) =>
                  setReaders(
                    readers.map((item) =>
                      item.id === reader.id ? { ...item, selected: event.target.checked } : item,
                    ),
                  )
                }
              />{' '}
              {reader.name} ({reader.email})
            </label>
          ))}
          {readers && (
            <button
              className="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void participationRequest(
                  `/api/participation/forms/${form.id}/readers`,
                  session,
                  'PUT',
                  {
                    version: readerVersion,
                    userIds: readers.filter((reader) => reader.selected).map((reader) => reader.id),
                  },
                )
                  .then(async () => {
                    const result = await participationRequest<{ form: ParticipationForm }>(
                      `/api/participation/forms/${form.id}`,
                      session,
                    );
                    adopt(result.form);
                    setReaders(undefined);
                    setNotice('Result readers updated.');
                  })
                  .catch((cause) => setError(cause.message))
                  .finally(() => setBusy(false));
              }}
            >
              Save result readers
            </button>
          )}
        </details>
      )}
      {error && (
        <p role="alert" className="inline-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="inline-success">
          {notice}
        </p>
      )}
    </main>
  );
}
function displayAnswer(question: ParticipationFormQuestion, value: unknown): string {
  if (value == null) return 'No answer';
  if ('options' in question) {
    if (question.type === 'availability' && typeof value === 'object')
      return question.options
        .map(
          (option) =>
            `${option.label}: ${(value as Record<string, string>)[option.id] || 'No answer'}`,
        )
        .join('; ');
    return question.options
      .filter((option) => (Array.isArray(value) ? value.includes(option.id) : value === option.id))
      .map((option) => option.label)
      .join(', ');
  }
  return String(value);
}

function PollTotals({
  questions,
  responses,
}: {
  questions: ParticipationFormQuestion[];
  responses: ParticipationFormResponse[];
}) {
  return (
    <section aria-label="Response totals">
      <h3>Response totals</h3>
      <p>
        Only answers to the current version are included. Multiple-choice totals may exceed the
        number of people responding.
      </p>
      {questions.map((question) => {
        const values = responses.map((response) => response.answers[question.id]);
        if ('options' in question)
          return (
            <section className="participation-totals" key={question.id}>
              <h4>{question.label}</h4>
              <table className="participation-results-table">
                <thead>
                  <tr>
                    <th scope="col">Option</th>
                    {question.type === 'availability' ? (
                      <>
                        <th scope="col">Yes</th>
                        <th scope="col">If needed</th>
                        <th scope="col">No</th>
                      </>
                    ) : (
                      <th scope="col">Votes</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {question.options.map((option) => (
                    <tr key={option.id}>
                      <th scope="row">{option.label}</th>
                      {question.type === 'availability' ? (
                        (['yes', 'if-needed', 'no'] as const).map((choice) => (
                          <td key={choice}>
                            {
                              values.filter(
                                (value) =>
                                  value &&
                                  typeof value === 'object' &&
                                  !Array.isArray(value) &&
                                  value[option.id] === choice,
                              ).length
                            }
                          </td>
                        ))
                      ) : (
                        <td>
                          {
                            values.filter((value) =>
                              Array.isArray(value)
                                ? value.includes(option.id)
                                : value === option.id,
                            ).length
                          }
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        if (question.type === 'rating') {
          const ratings = values.filter(
            (value): value is number => typeof value === 'number' && Number.isFinite(value),
          );
          return (
            <section className="participation-totals" key={question.id}>
              <h4>{question.label}</h4>
              <p>
                {ratings.length
                  ? `Average rating: ${(ratings.reduce((sum, value) => sum + value, 0) / ratings.length).toFixed(1)} (${ratings.length} answers)`
                  : 'No ratings yet.'}
              </p>
              <table className="participation-results-table">
                <thead>
                  <tr>
                    <th scope="col">Rating</th>
                    <th scope="col">Responses</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(
                    { length: question.max - question.min + 1 },
                    (_, index) => question.min + index,
                  ).map((rating) => (
                    <tr key={rating}>
                      <th scope="row">{rating}</th>
                      <td>{ratings.filter((value) => value === rating).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        }
        if (question.type === 'date') {
          const dates = [
            ...new Set(
              values.filter(
                (value): value is string => typeof value === 'string' && Boolean(value),
              ),
            ),
          ].sort();
          return (
            <section className="participation-totals" key={question.id}>
              <h4>{question.label}</h4>
              {dates.length ? (
                <table className="participation-results-table">
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Responses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dates.map((date) => (
                      <tr key={date}>
                        <th scope="row">{date}</th>
                        <td>{values.filter((value) => value === date).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No dates yet.</p>
              )}
            </section>
          );
        }
        return (
          <p key={question.id}>
            <strong>{question.label}:</strong>{' '}
            {values.filter((value) => typeof value === 'string' && value.trim()).length} written
            answers.
          </p>
        );
      })}
    </section>
  );
}
